// Date/time presentation for training sessions. Instants are rendered in the
// SESSION's own IANA zone when the caller passes one (a 2 PM Dar es Salaam
// session reads "2:00 PM" everywhere, next to a zone note when the viewer sits
// elsewhere); without a zone they fall back to the viewer's browser zone.
export const browserTimezone = (): string => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
};

/** A usable IANA zone or undefined (an unknown/blank zone falls back to the viewer's). */
export const safeTimeZone = (tz?: string | null): string | undefined => {
  if (!tz) return undefined;
  try { new Intl.DateTimeFormat('en', { timeZone: tz }); return tz; } catch { return undefined; }
};

/** Calendar-day parts of an instant in a zone. */
const dayPartsIn = (d: Date, tz: string) => {
  const parts = new Intl.DateTimeFormat('en', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value || 0);
  return { y: get('year'), m: get('month'), d: get('day') };
};

/**
 * The calendar day of an instant on the SESSION's clock, as a local Date at noon —
 * so org-format date helpers print the session's date (a 23:59 Dar due date must
 * not read as the day before in Chicago).
 */
export const dayInZone = (iso: string | Date, tz?: string | null): Date => {
  const d = iso instanceof Date ? iso : new Date(iso);
  const zone = safeTimeZone(tz);
  if (Number.isNaN(d.getTime()) || !zone) return d;
  const { y, m, d: day } = dayPartsIn(d, zone);
  return new Date(y, m - 1, day, 12, 0, 0, 0);
};

const sameDay = (a: Date, b: Date, tz?: string) => {
  if (tz) {
    const x = dayPartsIn(a, tz); const y = dayPartsIn(b, tz);
    return x.y === y.y && x.m === y.m && x.d === y.d;
  }
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

/**
 * "Tue, 3 Mar, 09:00 – 11:30" (same day) or "Tue, 3 Mar, 09:00 – Wed, 4 Mar, 11:30",
 * on the session's clock when `timeZone` is given, else the viewer's.
 */
export function formatSessionRange(startsAt: string, endsAt?: string | null, locale?: string, timeZone?: string | null): string {
  const s = new Date(startsAt);
  if (Number.isNaN(s.getTime())) return startsAt;
  const tz = safeTimeZone(timeZone);
  const dayOpts: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short', ...(tz ? { timeZone: tz } : {}) };
  const timeOpts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', ...(tz ? { timeZone: tz } : {}) };
  const start = `${s.toLocaleDateString(locale, dayOpts)}, ${s.toLocaleTimeString(locale, timeOpts)}`;
  if (!endsAt) return start;
  const e = new Date(endsAt);
  if (Number.isNaN(e.getTime())) return start;
  const end = sameDay(s, e, tz)
    ? e.toLocaleTimeString(locale, timeOpts)
    : `${e.toLocaleDateString(locale, dayOpts)}, ${e.toLocaleTimeString(locale, timeOpts)}`;
  return `${start} – ${end}`;
}

/** Short zone note, e.g. "Africa/Nairobi (EAT)" — only when it differs from the viewer's. */
export function sessionZoneNote(sessionTz?: string | null): string | null {
  if (!sessionTz || sessionTz === browserTimezone()) return null;
  try {
    const abbr = new Intl.DateTimeFormat('en', { timeZone: sessionTz, timeZoneName: 'short' })
      .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value;
    return abbr ? `${sessionTz} (${abbr})` : sessionTz;
  } catch { return sessionTz; }
}

/** yyyy-mm-dd of an instant in the viewer's zone (calendar deep-links). */
export const localDayOf = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** datetime-local input value (yyyy-mm-ddThh:mm) → ISO instant, '' → undefined. */
export const localInputToIso = (v: string): string | undefined => {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};
/** ISO instant → datetime-local input value in the viewer's zone. */
export const isoToLocalInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

export const isoDaysFromNow = (days: number): string => new Date(Date.now() + days * 86_400_000).toISOString();
